import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '@services/apiClient'
import { sessionStore } from '@features/auth/services/sessionStore'
import { preferencesStore } from '@features/preferences/services/preferencesStore'
import { loadVaultList, type VaultListResult, type VaultFlowDeps } from '../services/vaultFlow'
import { type VaultItem, type VaultItemType } from '../types'

/**
 * Vault 一覧画面の state machine + filter (T-D-Vault-A)。
 *
 * 設計判断:
 *   - SK は `sessionStore.getState()` から取得。LOCKED / UNAUTH では即 error 状態に。
 *   - filter は `'all' | 'favorite' | <type>` の 6 値。クライアント側で純粋に絞り込む
 *     (Folder filter は Phase 4 (T-D-Vault-Folder) 待ち、本 commit では非対応)。
 *   - reload は state を `'loading'` に戻さず、現在の items を保持したまま fetch する
 *     (UX: 削除後のリロードでチラつきを避ける)。失敗時のみ最新 items を保持。
 *   - error 文言は **i18n key** を返す (UI で `t()` を当てる、CLAUDE.md §2 ルール 10)。
 */

export type VaultFilter = 'all' | 'favorite' | VaultItemType

export const VAULT_FILTER_VALUES: readonly VaultFilter[] = [
  'all',
  'favorite',
  'login',
  'card',
  'note',
  'identity',
] as const

export type VaultListState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: readonly VaultItem[]; failureCount: number }
  | { kind: 'error'; messageKey: string }

const ERROR_KEYS = {
  no_session: 'vault.error.no_session',
  network: 'vault.error.network',
  generic: 'vault.error.generic',
} as const

const messageKeyFor = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return ERROR_KEYS.no_session

    return ERROR_KEYS.network
  }

  return ERROR_KEYS.generic
}

const applyFilter = (items: readonly VaultItem[], filter: VaultFilter): readonly VaultItem[] => {
  if (filter === 'all') return items
  if (filter === 'favorite') return items.filter(it => it.payload.favorite === true)

  return items.filter(it => it.type === filter)
}

const countBy = (items: readonly VaultItem[], filter: VaultFilter): number =>
  applyFilter(items, filter).length

export interface UseVaultListOptions {
  /** テスト用 DI: vaultFlow の deps を上書きする。 */
  flowDeps?: VaultFlowDeps
  /** フォルダ絞込 (T-D-Vault-Folder Phase 4)。null = 全フォルダ。 */
  activeFolderId?: string | null
  /**
   * Topbar 検索 query (T-E1)。trim + lowercase した非空文字なら、復号後の
   * name / username / URL / カスタムフィールド / note 本文等から部分一致で
   * フィルタする。
   */
  searchQuery?: string
}

/**
 * 検索対象の text を VaultItem から flat に抽出 (復号後の plaintext から)。
 * payload type ごとに検索したい主要フィールドを優先する。
 */
const searchableTextOf = (item: VaultItem): string => {
  const p = item.payload
  const parts: string[] = [p.name]
  // #1 カスタムフィールド: 名前は全 type で検索対象。text 型は値も含める
  // (hidden / boolean の値は機微 or 無意味なので名前のみ)。
  if (p.fields) {
    for (const f of p.fields) {
      parts.push(f.name)
      if (f.type === 'text' && f.value) parts.push(f.value)
    }
  }
  if (p.type === 'login') {
    if (p.username) parts.push(p.username)
    // #1 複数 URL を全て検索対象に (payload は復号時に uris[] へ正規化済)。
    if (p.uris) {
      for (const u of p.uris) parts.push(u.uri)
    }
    if (p.notes) parts.push(p.notes)
  } else if (p.type === 'card') {
    if (p.cardHolder) parts.push(p.cardHolder)
    if (p.notes) parts.push(p.notes)
  } else if (p.type === 'note') {
    if (p.body) parts.push(p.body)
  } else if (p.type === 'identity') {
    if (p.firstName) parts.push(p.firstName)
    if (p.lastName) parts.push(p.lastName)
    if (p.email) parts.push(p.email)
    if (p.phone) parts.push(p.phone)
    if (p.notes) parts.push(p.notes)
  }

  return parts.join(' ').toLowerCase()
}

export interface UseVaultList {
  state: VaultListState
  filter: VaultFilter
  setFilter: (f: VaultFilter) => void
  filtered: readonly VaultItem[]
  counts: Readonly<Record<VaultFilter, number>>
  reload: () => Promise<void>
}

export const useVaultList = (options: UseVaultListOptions = {}): UseVaultList => {
  const [state, setState] = useState<VaultListState>({ kind: 'loading' })
  const [filter, setFilter] = useState<VaultFilter>('all')

  // Track D: お気に入りを上に表示するユーザー preferences。subscribe で動的反映。
  const [favoritesOnTop, setFavoritesOnTop] = useState<boolean>(
    () => preferencesStore.getResolved().favoritesOnTop,
  )

  const mountedRef = useRef(true)
  const activeFolderId = options.activeFolderId ?? null
  const searchQuery = (options.searchQuery ?? '').trim().toLowerCase()

  useEffect(() => {
    return preferencesStore.subscribe(prefs => {
      setFavoritesOnTop(prefs.favoritesOnTop)
    })
  }, [])

  const fetchOnce = useCallback(async (): Promise<void> => {
    const session = sessionStore.getState()
    if (session.kind !== 'unlocked') {
      if (mountedRef.current) {
        setState({ kind: 'error', messageKey: ERROR_KEYS.no_session })
      }

      return
    }

    try {
      const result: VaultListResult = await loadVaultList(session.symmetricKey, options.flowDeps)
      if (!mountedRef.current) return
      setState({
        kind: 'ready',
        items: result.items,
        failureCount: result.failures.length,
      })
    } catch (err) {
      if (!mountedRef.current) return
      setState({ kind: 'error', messageKey: messageKeyFor(err) })
    }
  }, [options.flowDeps])

  useEffect(() => {
    mountedRef.current = true
    void fetchOnce()

    return () => {
      mountedRef.current = false
    }
  }, [fetchOnce])

  // state.kind が 'ready' でなければ毎回新規 `[]` を生成して
  // filtered / counts の useMemo が常に再計算されるのを防ぐため、items 自身を useMemo 化する。
  const items = useMemo<readonly VaultItem[]>(
    () => (state.kind === 'ready' ? state.items : []),
    [state],
  )

  const filtered = useMemo(() => {
    const byType = applyFilter(items, filter)

    const byFolder =
      activeFolderId !== null ? byType.filter(it => it.folderId === activeFolderId) : byType

    const bySearch =
      searchQuery === ''
        ? byFolder
        : byFolder.filter(it => searchableTextOf(it).includes(searchQuery))

    // Track D: favoritesOnTop=true なら favorite を先頭に安定ソート。
    // ※ ドメインルールによる行統合は廃止 (保存済みアイテムを隠してしまうため) —
    //   equivalent-domains はブラウザ拡張の autofill 用途で別途活用する。
    if (!favoritesOnTop) return bySearch

    return [...bySearch].sort((a, b) => {
      const av = a.payload.favorite === true ? 0 : 1
      const bv = b.payload.favorite === true ? 0 : 1

      return av - bv
    })
  }, [items, filter, activeFolderId, searchQuery, favoritesOnTop])

  const counts = useMemo<Record<VaultFilter, number>>(
    () => ({
      all: countBy(items, 'all'),
      favorite: countBy(items, 'favorite'),
      login: countBy(items, 'login'),
      card: countBy(items, 'card'),
      note: countBy(items, 'note'),
      identity: countBy(items, 'identity'),
    }),
    [items],
  )

  return {
    state,
    filter,
    setFilter,
    filtered,
    counts,
    reload: fetchOnce,
  }
}
